import {
  NullSoundPlayer,
  SineTonePlayer,
  SoundCatalog,
  SoundManager,
  type SoundCategory,
  type SoundDefinition,
  type SoundPlayer,
} from "@yjsgames/sound";
import {
  NOTE_HZ,
  NOTE_IDS_WEAK_TO_STRONG,
  strengthToNoteId,
} from "../objects/brickStrength.js";
import notesJson from "../data/generated/notes.json";
import soundsJson from "../data/generated/sounds.json";
import { MAX_VOLUME_LEVEL } from "../models/BrickSettings.js";

/** 이벤트 사운드 (전투/메타). sounds.tsv sfx 카테고리 행과 1:1 — id 일치는 단위테스트가 보증. */
export type SoundEvent =
  | "fire"
  | "hit"
  | "break"
  | "explosion"
  | "chain"
  | "shockwave"
  | "aftershock"
  | "box"
  | "acquire"
  | "levelup"
  | "rebirth";

/** UI 상호작용 사운드 id (sounds.tsv ui 카테고리). 결선: 버튼/탭/모달 공통 진입점. */
export const UI_SOUND_IDS = [
  "ui.button",
  "ui.tabOpen",
  "ui.modalOpen",
  "ui.modalClose",
  "ui.toggle",
] as const;
export type UiSoundId = (typeof UI_SOUND_IDS)[number];

/** sounds.json 1행 형태 (export 산출물 — file→url, toneHz/toneMs 0=미지정). */
interface SoundRow {
  readonly id: string;
  readonly category: string;
  readonly file: string;
  readonly volume: number;
  readonly toneHz: number;
  readonly toneMs: number;
  readonly loop: boolean;
  readonly enable: boolean;
}

/**
 * sounds.tsv 행(enable=true) → SoundDefinition. `file`→`url`(미래 mp3 교체용, 현재 빈칸),
 * `toneHz`/`toneMs`는 0이면 미지정(SineTonePlayer가 id 해시 톤 — 기존 hit/break 동작 보존),
 * >0이면 의도된 음정/길이로 재생(fire 110Hz, UI 사운드 등).
 */
function rowToDefinition(row: SoundRow): SoundDefinition {
  return {
    id: row.id,
    category: row.category as SoundCategory,
    url: row.file,
    volume: row.volume,
    loop: row.loop,
    toneHz: row.toneHz > 0 ? row.toneHz : undefined,
    toneMs: row.toneMs > 0 ? row.toneMs : undefined,
  };
}

/**
 * 사운드 정의 = TSV 외부화분(이벤트 + UI, sounds.json) + 코드 합성분(도레미 음계·멜로디).
 *
 * 음계/멜로디는 게임 로직과 SSoT를 공유(도레미=brickStrength NOTE_HZ, 멜로디=notes.tsv)하고
 * 동적 hz라 TSV로 옮기면 이중화되므로 코드 합성을 유지한다. 이벤트/UI 사운드만 데이터(TSV) 제어.
 */
const SOUND_DEFINITIONS: readonly SoundDefinition[] = [
  // 이벤트 + UI 사운드 (sounds.tsv, enable=true만) — fire/hit/.../ui.button/ui.tabOpen/...
  ...(soundsJson as readonly SoundRow[]).filter((r) => r.enable).map(rowToDefinition),
  // 벽돌 강도별 음계 비프 (도레미파솔라시) — toneHz로 음정 직접 지정 (SineTonePlayer).
  // 충돌 피드백의 주역 — 발사음(volume 0.05/45ms)보다 또렷하게 들리도록 볼륨·길이를 키운다.
  ...NOTE_IDS_WEAK_TO_STRONG.map(
    (id): SoundDefinition => ({
      id,
      category: "sfx",
      url: "",
      volume: 0.3,
      toneHz: NOTE_HZ[id],
      toneMs: 140,
    }),
  ),
  // 멜로디 챕터 음(2옥타브+반음, notes.tsv) — toneHz 동적 sine 재생. 강도 비프와 별도 채널.
  ...notesJson.map(
    (n): SoundDefinition => ({
      id: n.noteId,
      category: "sfx",
      url: "",
      volume: 0.2,
      toneHz: n.hz,
      toneMs: 150,
    }),
  ),
];

const SOUND_DEDUPE_WINDOW_MS = 20;
type NowProvider = () => number;

/**
 * 실제 mp3/wav 에셋이 없으므로 SineTonePlayer로 이벤트별 고유 톤(절차적 WebAudio sine)을 들려준다 (지시문 §22).
 * 에셋이 생기면 이 함수만 `new HtmlAudioSoundPlayer({ basePath: THEME.sounds.basePath })`로 교체하면
 * 파일 기반 재생으로 전환된다 (테마 매니페스트가 경로 소유 — 스킨별 사운드 교체 가능).
 * AudioContext가 없는 환경(SSR/test)은 NullSoundPlayer로 무음 fallback.
 */
function createDefaultPlayer(): SoundPlayer {
  const hasAudioContext =
    typeof AudioContext !== "undefined" ||
    (typeof window !== "undefined" && "webkitAudioContext" in window);
  return hasAudioContext ? new SineTonePlayer() : new NullSoundPlayer();
}

/**
 * 사운드 facade — 에셋 없이도 깨지지 않는다 (지시문 §22 "사운드는 선택 기능").
 *
 * 마스터 게인은 전체 음량 헤드룸(global.tsv `soundMasterGain`, 기본 3) — def.volume 가중치가
 * 전반적으로 작아 그대로면 작게 들리므로 일괄 증폭한다(상대 밸런스는 def.volume 유지). 사용자 설정은
 * 카테고리(bgm/sfx/ui) 그룹 볼륨을 0~10 레벨로 제어(설정 모달 슬라이더 → `setCategoryLevel`,
 * level/MAX_VOLUME_LEVEL이 0~1 그룹 볼륨). 현재 게임 사운드는 전부 `sfx` 카테고리이며 bgm/ui는
 * 카테고리 볼륨 인프라만 준비됨(해당 사운드 추가 시 자동 반영).
 *
 * AudioContext autoplay 정책 때문에 첫 사용자 제스처 전에는 무음 → unlockOnFirstGesture로 첫 클릭 시 활성화.
 */
export class SoundController {
  private readonly manager: SoundManager;
  private readonly now: NowProvider;
  private readonly lastPlayedAtByKey = new Map<string, number>();

  constructor(
    player: SoundPlayer = createDefaultPlayer(),
    masterGain = 1,
    now: NowProvider = Date.now,
  ) {
    this.manager = new SoundManager(SoundCatalog.fromDefinitions([...SOUND_DEFINITIONS]), player, {
      // 마스터 게인 = 전체 음량 헤드룸(global.tsv soundMasterGain). 사용자 볼륨은 그룹(setCategoryLevel).
      initialMaster: masterGain,
    });
    this.now = now;
    if (typeof document !== "undefined") {
      this.manager.unlockOnFirstGesture(document);
    }
  }

  play(event: SoundEvent): void {
    this.playDeduped(`event:${event}`, event);
  }

  /**
   * UI 상호작용 사운드 재생 (ui 카테고리 — 설정 모달 UI 볼륨 슬라이더가 자동 제어).
   * 버튼 클릭(ui.button)·탭 전환(ui.tabOpen)·모달 열림/닫힘(ui.modalOpen/Close) 공통 진입점에서 호출.
   */
  playUi(id: UiSoundId): void {
    this.playDeduped(`ui:${id}`, id);
  }

  /**
   * 벽돌 강도(strength01: 0=약~1=강)에 맞는 음계 비프 재생.
   * 약함=높은 음(시) → 강함=낮은 음(도). 벽돌 색상과 같은 strength에서 파생돼 색·음이 일관된다.
   */
  playBrickNote(strength01: number, pitchMultiplier = 1): void {
    const noteId = strengthToNoteId(strength01);
    if (SoundController.Verbose) {
      // [SoundController][V][playBrickNote] — 강도별 음 + 콤보 음정 배수 진단 (비프음은 자동 청취 불가).
      console.log(
        `[SoundController][V][playBrickNote] strength=${strength01.toFixed(3)} note=${noteId} hz=${NOTE_HZ[noteId]} pitch=${pitchMultiplier.toFixed(3)}`,
      );
    }
    this.playDeduped(`brick:${noteId}:pitch:${pitchMultiplier.toFixed(4)}`, noteId, {
      pitchMultiplier,
    });
  }

  /**
   * 멜로디 챕터 음 재생 — noteId(notes.tsv)를 toneHz 카탈로그로 재생. playBrickNote(강도 비프)와
   * 별도 채널. 블록 파괴 시 GameScene이 MelodySequencer.nextNote()로 넘긴 곡의 다음 음을 들려준다.
   */
  playMelodyNote(noteId: string): void {
    if (SoundController.Verbose) {
      // [SoundController][V][playMelodyNote] — 멜로디 음 재생 진단 (비프음 자동 청취 불가).
      console.log(`[SoundController][V][playMelodyNote] note=${noteId}`);
    }
    this.playDeduped(`melody:${noteId}`, noteId);
  }

  /**
   * 카테고리(bgm/sfx/ui) 볼륨을 0~10 레벨로 설정 — 설정 모달 볼륨 슬라이더에서 호출.
   * level/MAX_VOLUME_LEVEL(0~1)을 그룹 볼륨으로 매핑(SoundManager가 0~1 clamp + 활성 핸들에 일괄 반영).
   */
  setCategoryLevel(category: SoundCategory, level: number): void {
    this.manager.setGroupVolume(category, level / MAX_VOLUME_LEVEL);
  }

  /**
   * 마스터 음소거 토글 — 앱 백그라운드 전환(안드로이드 홈 이동) 시 전체 사운드를 즉시 끈다.
   * 그룹 볼륨(bgm/sfx/ui)과 직교라 복귀 시 unmute하면 사용자 볼륨 설정이 그대로 복원된다
   * (VolumeMixer에서 master×group 곱 — 그룹 레벨을 덮어쓰지 않음).
   */
  setMasterMuted(muted: boolean): void {
    this.manager.setMasterMuted(muted);
  }
  isMasterMuted(): boolean {
    return this.manager.isMasterMuted();
  }

  private playDeduped(
    idempotencyKey: string,
    soundId: string,
    options?: { pitchMultiplier?: number },
  ): void {
    const now = this.now();
    const lastPlayedAt = this.lastPlayedAtByKey.get(idempotencyKey);
    if (lastPlayedAt !== undefined) {
      const elapsed = now - lastPlayedAt;
      if (elapsed >= 0 && elapsed < SOUND_DEDUPE_WINDOW_MS) return;
    }
    this.lastPlayedAtByKey.set(idempotencyKey, now);
    this.manager.play(soundId, options);
  }

  /** 강도→음 매핑 진단 로그 게이트 (기본 off, verbose-logging 규약). */
  static Verbose = false;
}
